import type {
	BalanceSheetId,
	DataSource,
	FileId,
	FinancialPeriod,
	FinancialPeriodId,
	FinancialPeriodPreparationMethod,
	Formula,
	FormulaParseError,
	IncomeStatementId,
	ISODate,
	ISODateTime,
	PeriodType,
} from "@inrev/common";
import type { Result } from "@inrev/core";
import type { NonEmptyArray } from "../../../shared/types";

export type { NonEmptyArray };

/**
 * Resolves a raw adjustment draft into a validated, canonical Formula. Grids
 * supply a section-aware resolver (allowed-prefix enforcement) so the optimistic
 * preview and `ƒ` badge match what the server stores; the default is the
 * context-free `parseFormula`. `rowId` lets the resolver pick a per-row section
 * policy.
 */
export type NormalizeAdjustment = (
	rowId: string,
	draft: string,
) => Result<FormulaParseError, Formula>;

/**
 * Branded period column ids for the financial matrix grids. Balance sheet and income statement
 * grids key columns by their statement id; the analysis grid keys columns by financial period id.
 */
export type FinancialStatementPeriodId = BalanceSheetId | IncomeStatementId | FinancialPeriodId;

export enum ViewModeEnum {
	All = "all",
	GivenAllowed = "given_allowed",
	AllowedOnly = "allowed_only",
}
export type ViewMode = `${ViewModeEnum}`;

/** A selectable view-mode toggle in the grid toolbar. */
export type GridViewOption = { id: ViewMode; label: string };

/** Full set of view toggles available to admins (includes the editable adjustment column). */
export const ADMIN_VIEW_OPTIONS: NonEmptyArray<GridViewOption> = [
	{ id: ViewModeEnum.All, label: "All" },
	{ id: ViewModeEnum.GivenAllowed, label: "Given + Allowed" },
	{ id: ViewModeEnum.AllowedOnly, label: "Allowed only" },
];

/**
 * View toggles for read-only consumers (e.g. agents). Adjustments are never surfaced, so "All"
 * here means given + allowed side by side, and "Allowed" shows only the allowed values.
 */
export const READ_ONLY_VIEW_OPTIONS: NonEmptyArray<GridViewOption> = [
	{ id: ViewModeEnum.GivenAllowed, label: "All" },
	{ id: ViewModeEnum.AllowedOnly, label: "Allowed" },
];

/** Optional table-action toggles a statement grid can expose. */
export type GridTableActionToggle = "ppChange" | "hideEmptyRows";

export const ALL_GRID_TABLE_ACTION_TOGGLES: readonly GridTableActionToggle[] = [
	"ppChange",
	"hideEmptyRows",
];

/** Row id from a statement matrix view model (e.g. `bs::assets::current::cash::group`). */
export type FinancialMatrixRowId<TModel extends StatementMatrixViewModel> =
	TModel["rows"][number]["id"];

export type GivenOverrideArgs<
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> = {
	rowId: string;
	periodId: TPeriodId;
	value: number | null;
};

export type DeleteRowArgs = {
	rowId: string;
	label: string;
};

export type RenameRowArgs = {
	rowId: string;
	oldLabel: string;
	newLabel: string;
};

export type CreateRowArgs<
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> = {
	parentRowId: string;
	label: string;
	valuesByPeriod: Record<TPeriodId, number | null>;
	adjustmentsByPeriod: Record<TPeriodId, Formula | null>;
};

export type AdjustmentOverrideArgs<
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> = {
	rowId: string;
	periodId: TPeriodId;
	adjustment: Formula | null;
};

export type FinancialViewCell = {
	given: number | null;
	adjustment: FinancialPeriod.AdjustedLeaf | null;
	allowed: number | null;
	/**
	 * Pre-formatted string rendered verbatim in the allowed column in place of the currency-formatted
	 * `allowed` value. Used by the analysis grid for ratio strings (e.g. "1.53x", "42.0%") and for the
	 * "--" placeholder. A cell carrying a `displayValue` is treated as non-empty.
	 */
	displayValue?: string;
	/**
	 * Unit of the `allowed` magnitude, used by the P/P % change badge to format the native-unit
	 * fallback shown across a sign flip. Defaults to "dollars" when omitted.
	 */
	valueKind?: "dollars" | "ratio" | "percent" | "days";
	correction?: { kind: "value_changed" | "value_removed"; originalAmount: number };
};

export type RowKind =
	| "category"
	| "subtotal"
	| "editable"
	| "data"
	| "memo"
	| "label"
	| "spacer"
	| "boolean"
	| "derived";

export type BooleanCell = {
	given: boolean | null;
	adjustment: null;
	allowed: boolean | null;
};

export type DerivedFormula =
	| { kind: "sum_of_rows"; rowIds: string[] }
	| { kind: "equality_of_rows"; rowIds: [string, string] };

/**
 * Controls how the period-over-period change badge colors a row's delta.
 * - "higherIsBetter": an increase is favorable (green), a decrease unfavorable (red). The default.
 * - "lowerIsBetter": a decrease is favorable (green), an increase unfavorable (red) — e.g. liabilities.
 * - "neutral": always gray, regardless of direction.
 */
export type PpChangeColorMode = "higherIsBetter" | "lowerIsBetter" | "neutral";

export type FinancialViewRow<
	TPeriodId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> = {
	id: string;
	level: number;
	kind: RowKind;
	label: string;
	cells: Record<TPeriodId, FinancialViewCell>;
	booleanCells?: Record<TPeriodId, BooleanCell>;
	hideValuesWhenExpanded?: boolean;
	indentOffset?: number;
	allowsAddedLineItems?: boolean;
	/** Explicitly mark a row as non-editable (e.g. server-derived automated rows). Defaults to editable. */
	editable?: boolean;
	alwaysExpanded?: boolean;
	defaultCollapsed?: boolean;
	children?: FinancialViewRow<TPeriodId>[];
	derivedFrom?: DerivedFormula;
	/** How the P/P change badge colors this row's delta. Defaults to "higherIsBetter". */
	ppChangeColorMode?: PpChangeColorMode;
};

export type PeriodViewModel<TId extends FinancialStatementPeriodId = FinancialStatementPeriodId> = {
	id: TId;
	label: string;
	periodTypeLabel: string;
	periodEndDate?: ISODate;
	periodType?: PeriodType;
	preparationMethod?: FinancialPeriodPreparationMethod;
	sourceStatementDate?: ISODate;
	sourceDataSource?: DataSource;
	reviewedAt?: ISODateTime;
	fileId?: FileId | undefined;
	isActive?: boolean;
};

export type StatementMatrixViewModel<
	TId extends FinancialStatementPeriodId = FinancialStatementPeriodId,
> = {
	periods: PeriodViewModel<TId>[];
	rows: FinancialViewRow<TId>[];
};
